/**
 * Palette for the public /book-demo route.
 *
 * This route is intentionally DARK-ONLY (spec §4.2): the marketing surface has no
 * light/dark toggle, so there is no light counterpart to render. Every hex of the
 * designer's dark set lives here — nowhere else in the folder — as a Tailwind class
 * string, applied through `className={...}` interpolation. That is also how the
 * folder stays clean under `npm run darkmode-lint`: the linter reads colour
 * utilities out of literal strings inside a className attribute, and there are none.
 */
export const BD = {
  // Surfaces
  pageBg: 'bg-[#0A2133]',
  headerBg: 'bg-[#081C2C]',
  headerRing: 'shadow-[0px_0px_0px_1px_#1E3A4E]',
  cardBg: 'bg-[#0E2B42]',
  cardShadow: 'shadow-[0px_4px_16px_0px_#1018280D]',
  panelBg: 'bg-[#13354F]',
  inputBg: 'bg-[#081C2C]',
  inputShadow: 'shadow-[0_1px_2px_0_#1018280D]',
  rulerFrame: 'shadow-[0px_0px_0px_8px_#19405E] sm:shadow-[0px_0px_0px_15px_#19405E]',
  iconTileBg: 'bg-[#19405E]',
  hoverTile: 'hover:bg-[#19405E]',
  /** Muted mint used for the icons inside the recap tiles. */
  iconAccent: 'text-[#5EC4A8]',
  /** Soft tile behind the "what we unlock" icons — tinted fill plus the mock's hairline edge. */
  iconTileSoft: 'bg-[#28A8781A] border-[#00000005]',

  // Priority badges on the recap's "we'll focus on" tiles
  badgeHigh: 'text-[#32D583] border-[#0E8965] bg-[#16A34A26]',
  badgeMedium: 'text-[#F59E0B] border-[#F59E0B] bg-[#F59E0B26]',
  glowBg: 'bg-[#28A878]',

  // Accents
  accentBg: 'bg-[#28A878]',
  accentBgDeep: 'bg-[#0E8965]',
  accentSoftBg: 'bg-[#28A8781A]',
  accentText: 'text-[#28A878]',
  accentTextDeep: 'text-[#0E8965]',

  // Selection state
  selectedBorder: 'border-[#079455]',
  selectedBg: 'bg-[#0E8965]/40',
  selectedIconBg: 'bg-[#28A878] text-[#E8F7F2] border-[#00000005]',
  idleIconBg: 'bg-[#28A8781A] text-[#28A878] border-[#FFFFFF1A]',
  hoverBorder: 'hover:border-[#079455]/40',

  // Text
  textPrimary: 'text-[#F8FAFC]',
  textSecondary: 'text-[#CBD5E1]',
  textMuted: 'text-[#94A3B8]',
  textFaint: 'text-[#64748B]',
  textOnAccent: 'text-white',
  textOnAccentSoft: 'text-white/80',
  placeholder: 'placeholder:text-[#94A3B8]',

  // Borders / dividers
  border: 'border-[#1E3A4E]',
  borderStrong: 'border-[#2D4F68]',
  divider: 'bg-[#1E3A4E]',
  trackIdle: 'bg-[#0E2B42]',
  /** Ruler stop dot in its idle state (matches `textPrimary`). */
  stopDot: 'bg-[#F8FAFC]',

  // Buttons
  ghostButton: 'bg-[#14344C] border-[#2D4F68] text-[#EAF4F9] hover:bg-[#2D4F68]',
  backButton: 'border-[#28A878] text-[#0E8965] hover:bg-[#28a87814] shadow-[0_1px_2px_0_#1018280D]',
  primaryButton: 'bg-[#28A878] text-white disabled:bg-opacity-50',

  // Feedback
  errorText: 'text-[#FCA5A5]',
  errorBorder: 'border-[#B91C1C]',
  errorBox: 'bg-[#7F1D1D]/30 border-[#B91C1C]/50 text-[#FCA5A5]',
} as const;

/** Ruler-tick colours (inline styles — the ticks are absolutely positioned spans). */
export const BD_TICK_ACTIVE = '#28A878';
export const BD_TICK_IDLE = 'rgba(255, 255, 255, 0.08)';

/** Decorative overlays, kept as inline styles so no stylesheet fights the app layout. */
export const BD_OVERLAY_GRADIENT =
  'linear-gradient(180deg, rgba(5, 15, 20, 0.15) 0%, rgba(5, 15, 20, 0) 35%, rgba(5, 15, 20, 0.25) 70%, rgba(5, 15, 20, 0.55) 100%)';
export const BD_BANNER_STRIPES =
  'repeating-linear-gradient(-45deg, rgba(255, 255, 255, 0.04) 0px, rgba(255, 255, 255, 0.04) 1px, transparent 1px, transparent 10px)';
